import type { OfferDraftRowKind } from '../../../types';
import { formatEuro } from './offerDocumentUtils';

export function isOfferMetaRowKind(kind: OfferDraftRowKind): boolean {
  return kind === 'title' || kind === 'text' || kind === 'image';
}

export function isEventualFlag(value: unknown): boolean {
  return value === true;
}

export function contributesToOfferNetTotal(row: {
  kind: OfferDraftRowKind;
  isEventual?: boolean | null;
}): boolean {
  if (isOfferMetaRowKind(row.kind)) return false;
  return !isEventualFlag(row.isEventual);
}

export function lineTotalCents(qty: number, unit: number): number {
  return Math.round(qty * unit * 100) / 100;
}

/** Handwerk convention: parenthesize Gesamt so it is visibly excluded from the offer total. */
export function formatEventualGesamt(amount: number): string {
  return `(${formatEuro(amount)})`;
}

export const EVENTUAL_POSITION_HINT = 'Eventualposition, nicht in der Angebotssumme';

export function computeEventualExcludedNetSum(
  rows: Array<{
    kind: OfferDraftRowKind;
    isEventual?: boolean | null;
    quantity: number;
    unit_price_vk: number;
  }>,
): number {
  let sum = 0;
  for (const row of rows) {
    if (isOfferMetaRowKind(row.kind)) continue;
    if (!isEventualFlag(row.isEventual)) continue;
    sum += lineTotalCents(row.quantity, row.unit_price_vk);
  }
  return Math.round(sum * 100) / 100;
}

/**
 * Bosch OfficeOn / ToolTime: on invoice and work-report documents, Bedarfspositionen
 * become normal line items (enter the total). The offer itself keeps `is_eventual`.
 *
 * Abschlagsrechnung is special: percent/amount invoices a share of the awarded
 * offer net (Bedarf stays out). Position mode bills selected rows, including Bedarf.
 */
const DOCS_TREATING_EVENTUAL_AS_NORMAL = new Set([
  'Rechnung',
  'Schlussrechnung',
  'Stornorechnung',
  'Gutschrift',
  'Arbeitsbericht',
]);

export function documentTreatsEventualAsNormal(
  documentTypeLabel: string,
  opts?: { partialInvoiceMode?: 'percent' | 'amount' | 'position' | null },
): boolean {
  const label = String(documentTypeLabel ?? '').trim();
  if (label === 'Abschlagsrechnung') return opts?.partialInvoiceMode === 'position';
  return DOCS_TREATING_EVENTUAL_AS_NORMAL.has(label);
}

export function resolveMergedEventualFlag(
  draftValue: boolean | null | undefined,
  apiValue: boolean | null | undefined,
): boolean {
  if (draftValue !== undefined && draftValue !== null) return draftValue === true;
  return apiValue === true;
}
